import type { LoginMutationVariables, ResetPasswordMutationVariables, VerifyCustomerAccountMutationVariables } from '#graphql-operations'
import type { AuthPlugin } from '../plugins/1.auth'
import { deleteMe, login, logout, resetPassword } from '@layers/common/app/services/auth'
import { safeRedirectPath } from '../utils/redirect'

// The return type is annotated rather than inferred, and has to stay that way.
// Nuxt generates `interface NuxtApp extends InjectionType<typeof plugin>` for
// every plugin, so inferring what `$auth` is means resolving every plugin's
// provide — and those plugins call this composable. That closes a loop TypeScript
// cannot walk, and it gives up with `NuxtApp recursively references itself`,
// silently widening half the plugin layer to `any`.
//
// `AuthPlugin` is non-optional here only because `NuxtApp.$auth` already declares
// it so. At runtime the plugin bails with `return {}` on error pages, so `$auth`
// really can be undefined — which is why callers guard with `useAuth()?.`. Those
// guards are load-bearing; do not remove them because this type says they cannot fire.
export const useAuth = (): AuthPlugin => useNuxtApp().$auth

interface AuthLoginOptions {
  refreshActiveOrder?: () => Promise<unknown>
}

export async function authLogin(body: LoginMutationVariables, options: AuthLoginOptions = {}) {
  const localePath = useLocalePath()
  const route = useRoute()
  const activeOrderStore = useActiveOrderStore()
  const auth = useAuth()

  await login(body)

  const destination = safeRedirectPath(route.query.redirectTo, localePath('/account'))
  const orderRefreshes: Promise<unknown>[] = [activeOrderStore.refetchActiveOrder()]
  if (options.refreshActiveOrder)
    orderRefreshes.push(options.refreshActiveOrder())

  // Login can associate or merge the anonymous order with the customer. Settle
  // both cart views before the customer refresh triggers post-login navigation.
  const results = await Promise.allSettled(orderRefreshes)
  for (const result of results) {
    if (result.status === 'rejected')
      console.error('Failed to refresh the active order after login', result.reason)
  }

  await auth.refreshActiveCustomer()
  if (!auth.loggedIn.value)
    throw new Error('The signed-in customer could not be confirmed')
  // Honour where the user came from, otherwise land them on their account.
  await navigateTo(destination)
}

export async function verifyCustomerAccount(body: VerifyCustomerAccountMutationVariables) {
  const localePath = useLocalePath()
  // NOTE: This acts as authRegister, because email verification is required
  await useGraphqlMutation('verifyCustomerAccount', body)
  await useAuth().refreshActiveCustomer()
  const { redirectTo } = useRoute().query
  await navigateTo(safeRedirectPath(redirectTo, localePath('/')))
}

export async function authLogout() {
  const nuxtApp = useNuxtApp()
  const store = useStore()
  const activeOrderStore = useActiveOrderStore()
  const auth = useAuth()

  // Before the session ends, so what the app keeps in this browser for the
  // customer (the wishlist's guest lists) can be saved to the account and then
  // cleared for the next person.
  await nuxtApp.callHook('auth:logout')

  await logout()
  // The one place PostHog forgets the customer (see `resetPostHog`).
  resetPostHog()
  auth.clearActiveCustomer()

  // Broadcast logout to other tabs — once the session has ended, or their
  // re-check could still find this customer and keep showing them.
  if (import.meta.client && typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel('auth')
    channel.postMessage({ type: 'logout' })
    channel.close()
  }

  store.value.activeCustomer = store.value.activeOrder = null
  await Promise.all([auth.refreshActiveCustomer(), activeOrderStore.refetchActiveOrder()])
}

interface AuthDeleteAccountOptions {
  /**
   * Runs once the account is deleted, before the logout. Logging out clears the
   * active customer, which usually unmounts the caller, and Vue drops `emit`s
   * from unmounted components.
   */
  onDeleted?: () => void
}

export async function authDeleteAccount(options: AuthDeleteAccountOptions = {}) {
  await deleteMe()
  options.onDeleted?.()

  // The customer is gone, but its session is not: log out so the cookie, the
  // cached customer and the active order are cleared like on a normal sign-out.
  try {
    await authLogout()
  }
  catch (error) {
    console.error('Failed to log out after deleting the account', error)
    // `authLogout` bails before touching local state when the mutation fails;
    // don't leave the deleted customer shown as signed in.
    const store = useStore()
    store.value.activeCustomer = store.value.activeOrder = null
    await useAuth().refreshActiveCustomer()
  }
}

export async function authResetPassword(body: ResetPasswordMutationVariables) {
  const localePath = useLocalePath()
  await resetPassword(body)
  const { redirectTo } = useRoute().query
  await useAuth().refreshActiveCustomer()
  await navigateTo(safeRedirectPath(redirectTo, localePath('/')))
}
